import CustomLoading from '@/components/custom-loading';
import { Button } from '@/components/ui/button';
import {
  TransactionDetailFragment,
  useCrmCompanyQuery,
  useGetTransactionDetailQuery,
} from '@/gql/generated';
import { formatDateToTimezone } from '@/lib/timezone';
import { formatNumberToCurrency, formatPhoneNumber } from '@/lib/utils';
import { paymentMethods, transactionsType } from '@/utils/constant';
import { formatCodeGiftCard } from '@/utils/gift-card';
import {
  Document,
  Font,
  Image,
  Page,
  pdf,
  StyleSheet,
  Text,
  View,
} from '@react-pdf/renderer';
import { format } from 'date-fns';
import CustomError from '../_components/custom-error';

const OrderReceiptPDF = ({
  data,
  timeZone,
}: {
  data?: TransactionDetailFragment;
  timeZone: string;
}) => {
  const methods = data?.payment_methods[0];
  Font.register({
    family: 'Roboto',
    fonts: [
      {
        src: './Roboto-Medium.ttf',
        fontWeight: 'normal',
      },
      {
        src: './Roboto-Bold.ttf',
        fontWeight: 'bold',
      },
    ],
  });

  const styles = StyleSheet.create({
    page: {
      padding: 30,
      fontFamily: 'Roboto',
      color: '#333',
      fontSize: 12,
    },
  });

  return (
    <Document>
      <Page size={'A4'} style={styles.page}>
        {/* Company Info */}
        <View style={{ textAlign: 'center', marginBottom: '16px' }}>
          <Text style={{ fontWeight: 'bold', marginBottom: '16px' }}>
            {data?.company?.name}
          </Text>
          <Text>{data?.company?.address1}</Text>
          <Text>{formatPhoneNumber(data?.company?.business_phone ?? '')}</Text>
        </View>

        {/* Transaction Info */}
        <View>
          <Text style={{ fontWeight: 'bold' }}>
            Transaction ID: <Text>{data?.code ?? '/'}</Text>
          </Text>
          <Text>
            DateTime:{' '}
            <Text>
              {formatDateToTimezone(
                data?.date ?? '/',
                timeZone,
                'MM/dd/yyyy hh:mm a',
              )}
            </Text>
          </Text>
        </View>

        <View
          style={{
            borderBottomWidth: 1,
            borderBottomColor: '#ddd',
            borderBottomStyle: 'dashed',
            marginVertical: 10,
          }}
        />

        {/* Amount and Total */}
        <View style={{ justifyContent: 'space-between', flexDirection: 'row' }}>
          <Text>Gift Card:</Text>
          <Text>
            {data?.gift_card?.pos_gift_card?.code
              ? formatCodeGiftCard(data?.gift_card?.pos_gift_card?.code, false)
              : formatCodeGiftCard(data?.gift_card?.code ?? '', true)}
          </Text>
        </View>

        <View style={{ justifyContent: 'space-between', flexDirection: 'row' }}>
          <Text>Amount:</Text>
          <Text>
            {data?.type === transactionsType.TOP_UP
              ? formatNumberToCurrency(data?.price)
              : formatNumberToCurrency(data?.balance_change)}
          </Text>
        </View>

        <View style={{ justifyContent: 'space-between', flexDirection: 'row' }}>
          <Text>TOTAL:</Text>
          <Text>
            {data?.type === transactionsType.TOP_UP
              ? formatNumberToCurrency(data?.price)
              : formatNumberToCurrency(data?.balance_change)}
          </Text>
        </View>

        <View
          style={{
            borderBottomWidth: 1,
            borderBottomColor: '#ddd',
            borderBottomStyle: 'dashed',
            marginVertical: 10,
          }}
        />

        {/* Payment Details */}
        <View>
          <Text style={{ textAlign: 'center', fontWeight: 'bold' }}>
            Payment Details
          </Text>
          <Text>
            Action: {methods?.type === paymentMethods.CASH && 'Cash'}
            {methods?.type === paymentMethods.MANUAL_CARD_ENTRY &&
              'Manual Entered'}
            {methods?.type === paymentMethods.CHARGE && (
              <Text style={{ textTransform: 'capitalize' }}>
                {methods?.action}
              </Text>
            )}
            {data?.type === transactionsType.REDEMPTION && 'Gift Card'}
          </Text>
          <Text>
            Status: {methods?.type === paymentMethods.CASH && 'Cashed'}
            {methods?.type === paymentMethods.MANUAL_CARD_ENTRY && 'Sale'}
            {methods?.type === paymentMethods.CHARGE && 'Authorized'}
            {data?.type === transactionsType.REDEMPTION && 'Redemption'}
          </Text>
        </View>

        <View
          style={{
            borderBottomWidth: 1,
            borderBottomColor: '#ddd',
            borderBottomStyle: 'dashed',
            marginVertical: 10,
          }}
        />

        {/* Payment Method Details */}
        {methods?.type === paymentMethods.CHARGE && (
          <View>
            {/* <View style={{ border: 'dashed' }} /> */}
            <View>
              <Text style={{ textAlign: 'center', fontWeight: 'bold' }}>
                Payment Method
              </Text>
              <Text>
                CardHolder name: {methods?.card_info?.cardholder_name ?? '/'}
              </Text>
              <Text>Card type: {methods?.card_info?.card_type ?? '/'}</Text>
              <Text>
                Card last four:{' '}
                {methods?.card_info?.card_number_last_four ?? '/'}
              </Text>
            </View>
            <View
              style={{
                borderBottomWidth: 1,
                borderBottomColor: '#ddd',
                borderBottomStyle: 'dashed',
                marginVertical: 10,
              }}
            />

            <View>
              <Text style={{ textAlign: 'center', fontWeight: 'bold' }}>
                Transactions
              </Text>
              <Text>Auth code: {methods?.auth_code ?? '/'}</Text>
              <Text>Trans ID: {methods?.reference_id ?? '/'}</Text>
              <Text>Trans message: {methods?.trans_message ?? '/'}</Text>
            </View>
            <View
              style={{
                borderBottomWidth: 1,
                borderBottomColor: '#ddd',
                borderBottomStyle: 'dashed',
                marginVertical: 10,
              }}
            />
            <View style={{ marginBottom: '16px' }}>
              <Text style={{ textAlign: 'center', fontWeight: 'bold' }}>
                Device
              </Text>
              <Text>Serial Number: {methods?.device_sn ?? '/'}</Text>
              <Text>ID: {methods?.device_id ?? '/'}</Text>
            </View>
            {/* Signature Image */}
            {data?.signature?.base_uri && (
              <View>
                <Text>Signature:</Text>
                <Image
                  style={{ width: '300px', marginTop: '16px' }}
                  src={data.signature.base_uri}
                />
              </View>
            )}
          </View>
        )}

        {methods?.type === paymentMethods.MANUAL_CARD_ENTRY && (
          <View>
            {/* <View style={{ border: 'dashed' }} /> */}
            <View>
              <Text style={{ textAlign: 'center', fontWeight: 'bold' }}>
                Payment Method
              </Text>
              <Text>
                Expiration date:{' '}
                {methods &&
                  format(
                    new Date(methods?.card_info?.expiration_date ?? '/'),
                    'MM/yyyy',
                  )}
              </Text>
              <Text>
                Card last four:{' '}
                {methods.card_info?.card_number_last_four ?? '/'}
              </Text>
            </View>
            <View
              style={{
                borderBottomWidth: 1,
                borderBottomColor: '#ddd',
                borderBottomStyle: 'dashed',
                marginVertical: 10,
              }}
            />

            <View>
              <Text style={{ textAlign: 'center', fontWeight: 'bold' }}>
                Transactions
              </Text>
              <Text>Auth code: {methods?.auth_code ?? '/'}</Text>
              <Text>Trans ID: {methods?.reference_id ?? '/'}</Text>
              <Text>Trans message: {methods?.trans_message ?? '/'}</Text>
            </View>
            <View
              style={{
                borderBottomWidth: 1,
                borderBottomColor: '#ddd',
                borderBottomStyle: 'dashed',
                marginVertical: 10,
              }}
            />
            {/* Signature Image */}
            {data?.signature?.base_uri && (
              <View>
                <Text>Signature:</Text>
                <Image
                  style={{ width: '300px', marginTop: '16px' }}
                  src={data.signature.base_uri}
                />
              </View>
            )}
          </View>
        )}
      </Page>
    </Document>
  );
};

const OrderReceiptPage = () => {
  const urlEndcode = window.location.search.replace('?token=', '');

  const getTransactionDetailQuery = useGetTransactionDetailQuery({
    input_obj: {
      receipt_token: urlEndcode,
    },
  });
  const data = getTransactionDetailQuery.data?.get_transaction_detail;

  const crmCompanyQuery = useCrmCompanyQuery(
    {
      input_obj: { whmcs_id: Number(data?.whmcs_id) },
    },
    {
      enabled: !!data?.whmcs_id,
    },
  );

  const methods = data?.payment_methods[0];
  const timeZone = crmCompanyQuery.data?.crm_company?.time_zone;
  const handleDownload = async () => {
    const blob = await pdf(
      <OrderReceiptPDF data={data} timeZone={timeZone!} />,
    ).toBlob();
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'receipt.pdf';
    link.click();
  };

  if (getTransactionDetailQuery.isLoading || crmCompanyQuery.isLoading) {
    return (
      <div className="fixed left-0 top-0 size-full bg-slate-100/80">
        <div className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
          <CustomLoading />
        </div>
      </div>
    );
  }

  if (getTransactionDetailQuery.isError || crmCompanyQuery.isError) {
    return (
      <CustomError
        onRetry={() => {
          getTransactionDetailQuery.refetch();
          crmCompanyQuery.refetch();
        }}
        error={getTransactionDetailQuery.error ?? new Error('')}
      />
    );
  }
  return (
    <div className="flex h-full overflow-auto">
      {getTransactionDetailQuery.isSuccess && crmCompanyQuery.isSuccess && (
        <div className="m-10 mx-auto flex size-full max-w-xl flex-col p-4">
          <div className="text-center text-4xl font-bold">Receipt Details</div>
          <div className="my-4 border-b border-gray-300"></div>
          <div className="text-lg">
            <div className="pb-4 text-center">
              <div className="pb-3 text-3xl font-extrabold">
                {data?.company?.name}
              </div>
              <div>{data?.company?.address1}</div>
              <div>
                {formatPhoneNumber(data?.company?.business_phone ?? '')}
              </div>
            </div>

            <div className="text-lg font-semibold">
              Transaction ID: <span>{data?.code}</span>
            </div>
            <div>
              DateTime:{' '}
              <span className="text-gray-600">
                {formatDateToTimezone(
                  data?.date ?? '/',
                  timeZone!,
                  'MM/dd/yyyy hh:mm a',
                )}
              </span>
            </div>
            <div className="my-5 border-t-2 border-dashed border-gray-300"></div>
            <div className="flex items-center gap-1">
              <p className="font-medium">Gift Card:</p>
              <p className="text-lg font-bold">
                {data?.gift_card?.pos_gift_card?.code
                  ? formatCodeGiftCard(
                      data?.gift_card?.pos_gift_card?.code,
                      false,
                    )
                  : formatCodeGiftCard(data?.gift_card?.code ?? '', true)}
              </p>
            </div>
            <div className="flex items-center justify-between">
              <p className="font-medium">Amount:</p>
              <p className="text-lg font-bold text-green-800">
                {data?.type === transactionsType.TOP_UP
                  ? formatNumberToCurrency(data?.price)
                  : formatNumberToCurrency(data?.balance_change)}
              </p>
            </div>
            <div className="flex items-center justify-between">
              <p className="font-medium">TOTAL:</p>
              <p className="text-lg font-bold text-red-800">
                {data?.type === transactionsType.TOP_UP
                  ? formatNumberToCurrency(data?.price)
                  : formatNumberToCurrency(data?.balance_change)}
              </p>
            </div>
            <div className="my-5 border-t-2 border-dashed border-gray-300"></div>

            <p className="text-center text-2xl font-bold">Payment Details</p>
            <p>
              Action: {methods?.type === paymentMethods.CASH && 'Cash'}
              {methods?.type === paymentMethods.MANUAL_CARD_ENTRY &&
                'Manual Entered'}
              {methods?.type === paymentMethods.CHARGE && (
                <span className="capitalize">{methods?.action}</span>
              )}
              {data?.type === transactionsType.REDEMPTION && 'Gift Card'}
            </p>
            <p>
              Status: {methods?.type === paymentMethods.CASH && 'Cashed'}
              {methods?.type === paymentMethods.MANUAL_CARD_ENTRY && 'Sale'}
              {methods?.type === paymentMethods.CHARGE && 'Authorized'}
              {data?.type === transactionsType.REDEMPTION && 'Redemption'}
            </p>

            {methods?.type === paymentMethods.CHARGE && (
              <>
                <div className="my-5 border-t-2 border-dashed border-gray-300"></div>
                <p className="text-center text-2xl font-bold">Payment Method</p>
                <p>
                  CardHolder name: {methods?.card_info?.cardholder_name ?? '/'}
                </p>
                <p>Card type: {methods?.card_info?.card_type ?? '/'}</p>
                <div>
                  Card last four:{' '}
                  {methods?.card_info?.card_number_last_four ?? '/'}
                </div>
                <div className="my-5 border-t-2 border-dashed border-gray-300"></div>
                <p className="text-center text-2xl font-bold">Transactions</p>
                <p>Auth code: {methods?.auth_code ?? '/'}</p>
                <p>Trans ID: {methods?.reference_id ?? '/'}</p>
                <p>Trans message: {methods?.trans_message ?? '/'}</p>
                <div className="my-5 border-t-2 border-dashed border-gray-300"></div>
                <p className="text-center text-2xl font-bold">Device</p>
                <p>Serial Number: {methods?.device_sn ?? '/'}</p>
                <p>ID: {methods?.device_id ?? '/'}</p>
                <div>Signature:</div>
                {data?.signature?.base_uri && (
                  <img
                    className="pt-4"
                    src={data.signature?.base_uri}
                    alt={data.signature?.base_uri ?? ''}
                    width={300}
                    height={250}
                  />
                )}
              </>
            )}

            {methods?.type === paymentMethods.MANUAL_CARD_ENTRY && (
              <>
                <div className="my-5 border-t-2 border-dashed border-gray-300"></div>
                <p className="text-center text-2xl font-bold">Payment Method</p>
                <div>
                  Expiration date:{' '}
                  {methods &&
                    format(
                      new Date(methods?.card_info?.expiration_date ?? '/'),
                      'MM/yyyy',
                    )}
                </div>
                <div>
                  Card last four:{' '}
                  {methods?.card_info?.card_number_last_four ?? '/'}
                </div>
                <div className="my-5 border-t-2 border-dashed border-gray-300"></div>
                <p className="text-center text-2xl font-bold">Transactions</p>
                <p>Auth code: {methods?.auth_code ?? '/'}</p>
                <p>Trans ID: {methods?.reference_id ?? '/'}</p>
                <p>Trans message: {methods?.trans_message ?? '/'}</p>
                <div className="my-5 border-t-2 border-dashed border-gray-300"></div>
                <div>Signature:</div>
                {data?.signature?.base_uri && (
                  <img
                    className="pt-4"
                    src={data.signature?.base_uri}
                    alt={data.signature?.base_uri ?? ''}
                    width={300}
                    height={250}
                  />
                )}
              </>
            )}
          </div>
          <div className="w-full py-4">
            {getTransactionDetailQuery.isSuccess && (
              <Button
                onClick={handleDownload}
                className="mb-4 h-12 w-full rounded bg-blue-600 px-4 text-xl text-white transition duration-300 hover:bg-blue-700"
              >
                Download Receipt
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default OrderReceiptPage;
